/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable react/require-default-props */
import React, { FC, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '../../bootstrap/Modal';
import Icon from '../../icon/Icon';
import Spinner from '../../bootstrap/Spinner';
import './StatusChangeModal.scss';

export interface IStatusOption {
	value: string;
	label: string;
	dotColor?: string;
}

export interface IStatusChangeModalProps {
	isOpen: boolean;
	setIsOpen: (open: boolean) => void;
	title?: string;
	itemName?: string;
	currentStatus?: string;
	statusOptions?: IStatusOption[];
	onConfirm: (newStatus: string) => Promise<void> | void;
	onCancel?: () => void;
	isLoading?: boolean;
	confirmText?: string;
	cancelText?: string;
}

const DEFAULT_OPTIONS: IStatusOption[] = [
	{ value: 'ACTIVE', label: 'Active', dotColor: '#16a34a' },
	{ value: 'INACTIVE', label: 'Inactive', dotColor: '#d97706' },
];

export const StatusChangeModal: FC<IStatusChangeModalProps> = ({
	isOpen,
	setIsOpen,
	title = 'Change Status',
	itemName,
	currentStatus = 'ACTIVE',
	statusOptions = DEFAULT_OPTIONS,
	onConfirm,
	onCancel,
	isLoading = false,
	confirmText = 'Update Status',
	cancelText = 'Cancel',
}) => {
	const [selectedStatus, setSelectedStatus] = useState<string>(currentStatus);
	const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	// SYNC SELECTED STATUS: IF 2 OPTIONS, AUTO-SELECT ALTERNATE STATUS
	useEffect(() => {
		if (isOpen) {
			const currentNormalized = String(currentStatus || '').toUpperCase();
			if (statusOptions.length <= 2) {
				const alternate =
					statusOptions.find(
						(opt) => String(opt.value).toUpperCase() !== currentNormalized,
					) || statusOptions[0];
				setSelectedStatus(alternate?.value || currentStatus);
			} else {
				setSelectedStatus(currentStatus || statusOptions[0]?.value || 'ACTIVE');
			}
			setIsDropdownOpen(false);
		}
	}, [isOpen, currentStatus, statusOptions]);

	const handleClose = () => {
		if (isLoading) return;
		setIsDropdownOpen(false);
		if (onCancel) onCancel();
		setIsOpen(false);
	};

	const handleConfirm = async () => {
		await onConfirm(selectedStatus);
	};

	// AUTO-FOCUS ON SELECTED STATUS PILL ON OPEN
	useEffect(() => {
		if (!isOpen) return undefined;

		const timer = setTimeout(() => {
			const pill =
				document.getElementById(`status-change-pill-${selectedStatus}`) ||
				document.querySelector<HTMLElement>('.status-binary-pill.is-selected') ||
				document.querySelector<HTMLElement>('.status-binary-pill') ||
				document.querySelector<HTMLElement>('.status-dropdown-control');
			pill?.focus();
		}, 100);
		return () => clearTimeout(timer);
	}, [isOpen, selectedStatus]);

	// KEYBOARD NAVIGATION (TAB, ARROWS, ENTER, ESCAPE)
	useEffect(() => {
		if (!isOpen || isLoading) return undefined;

		const handleKeyDown = (e: KeyboardEvent) => {
			const activeEl = document.activeElement;
			const isSubmitBtn = activeEl?.classList.contains('btn-status-accept');
			const isCancelBtn = activeEl?.classList.contains('btn-status-cancel');
			const isStatusControl =
				activeEl?.classList.contains('status-binary-pill') ||
				activeEl?.classList.contains('status-dropdown-control') ||
				activeEl?.closest('.status-binary-toggle-container') !== null ||
				activeEl?.closest('.status-dropdown-wrapper') !== null;

			// TAB NAVIGATION
			if (e.key === 'Tab') {
				if (!e.shiftKey) {
					if (isStatusControl) {
						e.preventDefault();
						document.querySelector<HTMLElement>('.btn-status-accept')?.focus();
					} else if (isSubmitBtn) {
						e.preventDefault();
						document.querySelector<HTMLElement>('.btn-status-cancel')?.focus();
					} else if (isCancelBtn) {
						e.preventDefault();
						const targetPill =
							document.getElementById(`status-change-pill-${selectedStatus}`) ||
							document.querySelector<HTMLElement>('.status-binary-pill.is-selected') ||
							document.querySelector<HTMLElement>('.status-dropdown-control');
						targetPill?.focus();
					}
				} else if (isStatusControl) {
					e.preventDefault();
					document.querySelector<HTMLElement>('.btn-status-cancel')?.focus();
				} else if (isSubmitBtn) {
					e.preventDefault();
					const targetPill =
						document.getElementById(`status-change-pill-${selectedStatus}`) ||
						document.querySelector<HTMLElement>('.status-binary-pill.is-selected') ||
						document.querySelector<HTMLElement>('.status-dropdown-control');
					targetPill?.focus();
				} else if (isCancelBtn) {
					e.preventDefault();
					document.querySelector<HTMLElement>('.btn-status-accept')?.focus();
				}
				return;
			}

			if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
				e.preventDefault();
				const currentIndex = statusOptions.findIndex((opt) => opt.value === selectedStatus);
				const nextIndex = (currentIndex + 1) % statusOptions.length;
				const nextVal = statusOptions[nextIndex].value;
				setSelectedStatus(nextVal);
				setTimeout(() => {
					document.getElementById(`status-change-pill-${nextVal}`)?.focus();
				}, 20);
			} else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
				e.preventDefault();
				const currentIndex = statusOptions.findIndex((opt) => opt.value === selectedStatus);
				const prevIndex = (currentIndex - 1 + statusOptions.length) % statusOptions.length;
				const prevVal = statusOptions[prevIndex].value;
				setSelectedStatus(prevVal);
				setTimeout(() => {
					document.getElementById(`status-change-pill-${prevVal}`)?.focus();
				}, 20);
			} else if (e.key === 'Enter') {
				e.preventDefault();
				if (isCancelBtn) {
					handleClose();
				} else {
					handleConfirm();
				}
			} else if (e.key === 'Escape') {
				e.preventDefault();
				handleClose();
			}
		};

		if (isOpen) {
			window.addEventListener('keydown', handleKeyDown);
		}
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, isLoading, selectedStatus, statusOptions]);

	// HANDLE CLICK OUTSIDE DROPDOWN
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
				setIsDropdownOpen(false);
			}
		};
		if (isDropdownOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isDropdownOpen]);

	const selectedOption =
		statusOptions.find((opt) => opt.value === selectedStatus) ||
		statusOptions[0] || { value: selectedStatus, label: selectedStatus, dotColor: '#64748b' };

	const hasMultipleStatuses = statusOptions.length > 2;

	return (
		<Modal
			isOpen={isOpen}
			setIsOpen={setIsOpen}
			isCentered
			className='status-change-modal'
			isStaticBackdrop={isLoading}>
			{/* MODAL HEADER */}
			<ModalHeader className='status-modal-header'>
				<ModalTitle id='status-modal-title' className='status-modal-title'>
					{title}
				</ModalTitle>
				<button
					type='button'
					className='btn-modal-close'
					aria-label='Close'
					disabled={isLoading}
					onClick={handleClose}>
					<Icon icon='Close' size='sm' />
				</button>
			</ModalHeader>

			{/* MODAL BODY */}
			<ModalBody className='status-modal-body'>
				{itemName && (
					<p className='status-target-item-text'>
						Updating status for: <strong className='text-dark'>{itemName}</strong>
					</p>
				)}

				<span className='status-dropdown-label d-block'>
					Select Status <span className='text-danger'>*</span>
				</span>

				{/* IF <= 2 STATUS OPTIONS: CLEAN BINARY PILL SELECTOR WITH ALTERNATE PRE-SELECTED */}
				{!hasMultipleStatuses ? (
					<div className='status-binary-toggle-container'>
						{statusOptions.map((opt) => {
							const isSelected = selectedStatus === opt.value;
							return (
								<button
									key={opt.value}
									id={`status-change-pill-${opt.value}`}
									type='button'
									disabled={isLoading}
									className={`status-binary-pill ${isSelected ? 'is-selected' : ''}`}
									onClick={() => setSelectedStatus(opt.value)}>
									{opt.dotColor && (
										<span
											className='status-dot'
											style={{ backgroundColor: opt.dotColor }}
										/>
									)}
									<span className='pill-label'>{opt.label}</span>
									{isSelected && <Icon icon='Check' size='sm' className='ms-1' />}
								</button>
							);
						})}
					</div>
				) : (
					/* IF > 2 STATUS OPTIONS: DROPDOWN SELECTOR */
					<div className='status-dropdown-wrapper' ref={dropdownRef}>
						<button
							type='button'
							disabled={isLoading}
							className={`status-dropdown-control ${isDropdownOpen ? 'is-open' : ''}`}
							onClick={() => setIsDropdownOpen((prev) => !prev)}>
							<div className='status-control-value'>
								{selectedOption.dotColor && (
									<span
										className='status-dot'
										style={{ backgroundColor: selectedOption.dotColor }}
									/>
								)}
								<span className='status-label-text'>{selectedOption.label}</span>
							</div>
							<span className={`status-arrow-icon ${isDropdownOpen ? 'is-open' : ''}`}>
								<Icon icon='KeyboardArrowDown' size='sm' />
							</span>
						</button>

						{isDropdownOpen && (
							<div className='status-dropdown-menu'>
								{statusOptions.map((opt) => (
									<button
										key={opt.value}
										type='button'
										className={`status-dropdown-option ${selectedStatus === opt.value ? 'is-selected' : ''}`}
										onClick={() => {
											setSelectedStatus(opt.value);
											setIsDropdownOpen(false);
										}}>
										<div className='d-flex align-items-center gap-2'>
											{opt.dotColor && (
												<span
													className='status-dot'
													style={{ backgroundColor: opt.dotColor }}
												/>
											)}
											<span className='option-text'>{opt.label}</span>
										</div>
										{selectedStatus === opt.value && (
											<span className='status-check-icon'>
												<Icon icon='Check' size='sm' />
											</span>
										)}
									</button>
								))}
							</div>
						)}
					</div>
				)}
			</ModalBody>

			{/* MODAL FOOTER */}
			<ModalFooter className='status-modal-footer'>
				{/* CANCEL BUTTON */}
				<button
					type='button'
					className='btn-status-cancel'
					disabled={isLoading}
					onClick={handleClose}>
					<Icon icon='Close' size='sm' />
					<span>{cancelText}</span>
				</button>

				{/* CONFIRM / UPDATE BUTTON */}
				<button
					type='button'
					className='btn-status-accept'
					disabled={isLoading}
					onClick={handleConfirm}>
					{isLoading ? (
						<>
							<Spinner size='sm' isGrow={false} />
							<span>Updating...</span>
						</>
					) : (
						<>
							<Icon icon='Check' size='sm' />
							<span>{confirmText}</span>
						</>
					)}
				</button>
			</ModalFooter>
		</Modal>
	);
};

(StatusChangeModal as any).propTypes = {
	isOpen: PropTypes.bool.isRequired,
	setIsOpen: PropTypes.func.isRequired,
	title: PropTypes.string,
	itemName: PropTypes.string,
	currentStatus: PropTypes.string,
	statusOptions: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			dotColor: PropTypes.string,
		}),
	),
	onConfirm: PropTypes.func.isRequired,
	onCancel: PropTypes.func,
	isLoading: PropTypes.bool,
	confirmText: PropTypes.string,
	cancelText: PropTypes.string,
};

StatusChangeModal.defaultProps = {
	title: 'Change Status',
	itemName: undefined,
	currentStatus: 'ACTIVE',
	statusOptions: DEFAULT_OPTIONS,
	onCancel: undefined,
	isLoading: false,
	confirmText: 'Update Status',
	cancelText: 'Cancel',
};

export default StatusChangeModal;
